//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import moment from 'moment-timezone';

import { IAliasedDateRange } from '@components/date-range-picker/dateRangePickerModels';
import DateString from '@models/DateString';
import TimeZoneId from '@models/TimeZoneId';
import { IUnisonDateRange } from '@models/UnisonTypes';

export const monthDayYearDateFormat = 'MMM DD, YYYY';

export const timeMonthDayYearFormat = 'h:mm a [on] MMM DD, YYYY';

export const monthDayYearTimeTimezoneFormat = 'MMM DD, YYYY [at] h:mm A z';

export const parseDateRangeStringFromFilter = (dateRange: IUnisonDateRange) => {
  const alias = dateRange.alias;

  switch (alias) {
    case 'custom':
      return buildCustomDateRangeString(
        dateRange.startDate,
        dateRange.endDate,
        '/',
      );
    case 'last24Hours':
    case 'last7Days':
    case 'last30Days':
    case 'last3Months':
      return alias;
    default:
      return '';
  }
};

export const buildCustomDateRangeString = (
  startDate: Date,
  endDate: Date,
  separator: string,
  format: string = 'YYYY-MM-D',
) => {
  const from = moment(startDate).format(format);
  const to = moment(endDate).format(format);

  return `${from}${separator}${to}`;
};

/**
 * Create an english expression of the date range.
 * e.g. "[No stories found] in the last 7 days"
 * e.g. "[No stories found] between 3/12/12 and 3/45/45"
 */
export const dateRangeExpression = (dateRange: IUnisonDateRange) => {
  switch (dateRange.alias) {
    case 'allTime':
      return '';
    case 'last3Months':
      return ' in the last 3 months';
    case 'today':
      return ' today';
    case 'custom': {
      // Unison Date Ranges are always in browser timezone.
      const startDate = moment(dateRange.startDate).format('ll');
      const endDate = moment(dateRange.endDate).format('ll');
      if (startDate === endDate) {
        return ` on ${startDate}`;
      } else {
        return ` between ${startDate} and ${endDate}`;
      }
    }
    default: {
      const res = /last(\d+)Days/.exec(dateRange.alias);
      if (res) {
        const [, dayCount] = res;
        return ` in the last ${dayCount} days`;
      }
      // Unknown alias, please add an explicit case!
      return '';
    }
  }
};

/**
 * Checks if the given `date` is within the current week,
 * according to the user's time zone
 */
export const isCurrentWeek = (date: Date, timeZone: TimeZoneId): boolean =>
  DateString.getDayOffset(
    DateString.getToday(timeZone),
    DateString.fromTimestamp(date, timeZone),
  ) < 7;

/**
 * @param {string} isoDate - The date to format
 * @param {TimeZoneId} userTimeZone - The timezone Id
 * @param {string} format - The format to use to eg: 'h:mm a [on] MMM DD, YYYY'
 *
 */
export const formatDateWithTime = (
  isoDate: string | number,
  userTimeZone: TimeZoneId,
  format: string,
) => {
  const dateTime = moment.tz(isoDate, userTimeZone);
  return dateTime.format(format);
};

/**
 * Formats a given `date` string in the format of
 *  "Month Day, Year" (e.g., "January 1, 2020").
 *
 * @param {string} date - The date to format.
 * @returns {string} The formatted date string.
 */
export const formatDate = (date: string): string => {
  const formattedDate = new Date(date).toLocaleString('default', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  return formattedDate;
};

/**
 * Date regex to match a valid date in the format 'MM/DD/YYYY'
 */
export const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;

/**
 * Given a string, returns a UTC date if the string is a valid date in the format 'MM/DD/YYYY'
 *
 * @param dateString A string to parse as a date
 * @returns A date if the string is a valid date, otherwise undefined
 * @example parseDate('12/31/2021') // returns a Date object
 * @example parseDate('12/31/21') // returns undefined
 * @example parseDate('12/31/2021 12:00:00') // returns undefined
 */
export const parseOptionalUTCDate = (
  dateString?: string,
  defaultValue?: Date,
): Date | undefined => {
  if (dateString && dateRegex.test(dateString)) {
    return moment.utc(dateString, 'MM/DD/YYYY').toDate();
  }
  return defaultValue ? moment(defaultValue).utc().toDate() : undefined;
};

/**
 * Converts an IAliasedDateRange into start and end timestamps (in milliseconds),
 * while respecting the given time zone.
 */
export const getTimestampsFromAliasedDateRange = (
  dateRange: IAliasedDateRange,
): {
  startTimestamp: number;
  endTimestamp: number;
} => {
  const { endDate, startDate, timeZone } = dateRange;

  return {
    startTimestamp: moment.tz(startDate, timeZone).toDate().getTime(),
    endTimestamp: moment.tz(endDate, timeZone).toDate().getTime(),
  };
};
